/** Options a caller may pass. All readonly — a client never mutates its caller's object. */
export interface RequestOptions {
  readonly headers?: Readonly<Record<string, string>>
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>
  readonly signal?: AbortSignal
  readonly timeoutMs?: number
  /**
   * Do not attempt a session refresh if this request 401s.
   *
   * Set on the refresh call itself, and on the retry that follows one, so a dead session
   * cannot produce an endless refresh → 401 → refresh loop.
   */
  readonly skipRefresh?: boolean
}

/**
 * The error every failure becomes, so a caller has one thing to catch.
 *
 * `status: 0` is deliberate for a transport failure — DNS, refused, offline, CORS. It sits
 * **below every `>= 400` check** a caller makes, so "the network did not happen" can never
 * be mistaken for "the server said no".
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    message: string,
    readonly details?: Readonly<Record<string, readonly string[]>>,
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** A 422 with a field bag — the only shape `useFormErrors` renders per field. */
  get isValidation(): boolean {
    return this.status === 422
  }

  /** The session is gone. Handled once, globally, never per call site. */
  get isUnauthenticated(): boolean {
    return this.status === 401 || this.code === 'UNAUTHENTICATED'
  }
}
